import { schema, denormalize } from 'normalizr';
import { createAction } from 'redux-api-middleware';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { handleResponse, handleError, buildQueryString } from '../utils';
import { contractSchema } from './schemas';

export const FETCH_CONTRACT_REQUEST = 'FETCH_CONTRACT_REQUEST';
export const FETCH_CONTRACT_SUCCESS = 'FETCH_CONTRACT_SUCCESS';
export const FETCH_CONTRACT_FAILURE = 'FETCH_CONTRACT_FAILURE';
export const FETCH_CONTRACTS_REQUEST = 'FETCH_CONTRACTS_REQUEST';
export const FETCH_CONTRACTS_SUCCESS = 'FETCH_CONTRACTS_SUCCESS';
export const FETCH_CONTRACTS_UPDATE_SUCCESS = 'FETCH_CONTRACTS_UPDATE_SUCCESS';
export const FETCH_CONTRACTS_FAILURE = 'FETCH_CONTRACTS_FAILURE';
export const FETCH_CONTRACTPRODUCTS_REQUEST = 'FETCH_CONTRACTPRODUCTS_REQUEST';
export const FETCH_CONTRACTPRODUCTS_SUCCESS = 'FETCH_CONTRACTPRODUCTS_SUCCESS';
export const FETCH_CONTRACTPRODUCTS_FAILURE = 'FETCH_CONTRACTPRODUCTS_FAILURE';
export const FETCH_CONTRACTACTIVITY_REQUEST = 'FETCH_CONTRACTACTIVITY_REQUEST';
export const FETCH_CONTRACTACTIVITY_SUCCESS = 'FETCH_CONTRACTACTIVITY_SUCCESS';
export const FETCH_CONTRACTACTIVITY_FAILURE = 'FETCH_CONTRACTACTIVITY_FAILURE';
export const FETCH_ASSOCIATED_PRODUCTS_REQUEST = 'FETCH_ASSOCIATED_PRODUCTS_REQUEST';
export const FETCH_ASSOCIATED_PRODUCTS_SUCCESS = 'FETCH_ASSOCIATED_PRODUCTS_SUCCESS';
export const FETCH_ASSOCIATED_PRODUCTS_FAILURE = 'FETCH_ASSOCIATED_PRODUCTS_FAILURE';
export const CREATE_CONTRACT_REQUEST = 'CREATE_CONTRACT_REQUEST';
export const CREATE_CONTRACT_SUCCESS = 'CREATE_CONTRACT_SUCCESS';
export const CREATE_CONTRACT_FAILURE = 'CREATE_CONTRACT_FAILURE';
export const EDIT_CONTRACT_REQUEST = 'EDIT_CONTRACT_REQUEST';
export const EDIT_CONTRACT_SUCCESS = 'EDIT_CONTRACT_SUCCESS';
export const EDIT_CONTRACT_FAILURE = 'EDIT_CONTRACT_FAILURE';
export const SUBMIT_FOR_APPROVAL_REQUEST = 'SUBMIT_FOR_APPROVAL_REQUEST';
export const SUBMIT_FOR_APPROVAL_SUCCESS = 'SUBMIT_FOR_APPROVAL_SUCCESS';
export const SUBMIT_FOR_APPROVAL_FAILURE = 'SUBMIT_FOR_APPROVAL_FAILURE';
export const UPLOAD_CONTRACT_ATTACHMENT_REQUEST = 'UPLOAD_CONTRACT_ATTACHMENT_REQUEST';
export const UPLOAD_CONTRACT_ATTACHMENT_SUCCESS = 'UPLOAD_CONTRACT_ATTACHMENT_SUCCESS';
export const UPLOAD_CONTRACT_ATTACHMENT_FAILURE = 'UPLOAD_CONTRACT_ATTACHMENT_FAILURE';
export const APPROVE_CONTRACT_REQUEST = 'APPROVE_CONTRACT_REQUEST';
export const APPROVE_CONTRACT_SUCCESS = 'APPROVE_CONTRACT_SUCCESS';
export const APPROVE_CONTRACT_FAILURE = 'APPROVE_CONTRACT_FAILURE';
export const REJECT_CONTRACT_REQUEST = 'REJECT_CONTRACT_REQUEST';
export const REJECT_CONTRACT_SUCCESS = 'REJECT_CONTRACT_SUCCESS';
export const REJECT_CONTRACT_FAILURE = 'REJECT_CONTRACT_FAILURE';
export const PAUSE_CONTRACT_REQUEST = 'PAUSE_CONTRACT_REQUEST';
export const PAUSE_CONTRACT_SUCCESS = 'PAUSE_CONTRACT_SUCCESS';
export const PAUSE_CONTRACT_FAILURE = 'PAUSE_CONTRACT_FAILURE';
export const UNPAUSE_CONTRACT_REQUEST = 'UNPAUSE_CONTRACT_REQUEST';
export const UNPAUSE_CONTRACT_SUCCESS = 'UNPAUSE_CONTRACT_SUCCESS';
export const UNPAUSE_CONTRACT_FAILURE = 'UNPAUSE_CONTRACT_FAILURE';
export const DISCARD_CONTRACT_DRAFT_REQUEST = 'DISCARD_CONTRACT_DRAFT_REQUEST';
export const DISCARD_CONTRACT_DRAFT_SUCCESS = 'DISCARD_CONTRACT_DRAFT_SUCCESS';
export const DISCARD_CONTRACT_DRAFT_FAILURE = 'DISCARD_CONTRACT_DRAFT_FAILURE';
export const PATCH_CONTRACT_FILTERS_REQUEST = 'PATCH_CONTRACT_FILTERS_REQUEST';
export const PATCH_CONTRACT_FILTERS_SUCCESS = 'PATCH_CONTRACT_FILTERS_SUCCESS';
export const PATCH_CONTRACT_FILTERS_FAILURE = 'PATCH_CONTRACT_FILTERS_FAILURE';
export const RE_ORDER_AGREEMENTS_CONTRACT_REQUEST = 'RE_ORDER_AGREEMENTS_CONTRACT_REQUEST';
export const RE_ORDER_AGREEMENTS_CONTRACT_SUCCESS = 'RE_ORDER_AGREEMENTS_CONTRACT_SUCCESS';
export const RE_ORDER_AGREEMENTS_CONTRACT_FAILURE = 'RE_ORDER_AGREEMENTS_CONTRACT_FAILURE';
export const SAVE_FALLBACK_AGREEMENT_REQUEST = 'SAVE_FALLBACK_AGREEMENT_REQUEST';
export const SAVE_FALLBACK_AGREEMENT_SUCCESS = 'SAVE_FALLBACK_AGREEMENT_SUCCESS';
export const SAVE_FALLBACK_AGREEMENT_FAILURE = 'SAVE_FALLBACK_AGREEMENT_FAILURE';
export const UPDATE_FALLBACK_AGREEMENT_REQUEST = 'UPDATE_FALLBACK_AGREEMENT_REQUEST';
export const UPDATE_FALLBACK_AGREEMENT_SUCCESS = 'UPDATE_FALLBACK_AGREEMENT_SUCCESS';
export const UPDATE_FALLBACK_AGREEMENT_FAILURE = 'UPDATE_FALLBACK_AGREEMENT_FAILURE';
export const RE_ORDER_ADVANCEGROUPS_CONTRACT_REQUEST = 'RE_ORDER_ADVANCEGROUPS_CONTRACT_REQUEST';
export const RE_ORDER_ADVANCEGROUPS_CONTRACT_SUCCESS = 'RE_ORDER_ADVANCEGROUPS_CONTRACT_SUCCESS';
export const RE_ORDER_ADVANCEGROUPS_CONTRACT_FAILURE = 'RE_ORDER_ADVANCEGROUPS_CONTRACT_FAILURE';

export const CONTRACT_PAGE_SIZE = 20;
export const activitySchema = new schema.Entity('activity', {}, { idAttribute: '_id' });
export const productSchema = new schema.Entity(
  'associatedProducts',
  {},
  { idAttribute: 'tokenId' },
);

export const useSelectContractByAddress = contractAddress =>
  useSelector(state =>
    denormalize(
      get(state, `entities.contracts.${contractAddress}`, []),
      contractSchema,
      state.entities || {},
    ),
  );

/**
 * Fetch single contract related to a specific partner.
 *
 * @param {string} address - Contract address.
 * @param {object} [options] - Options for fetching.
 * @param {boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchContract = (address, options = { useCache: true }) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${address}`,
    method: 'GET',
    bailout: store => options.useCache && get(store, `entities.contracts.${address}`, false),
    types: [
      { type: FETCH_CONTRACT_REQUEST },
      {
        type: FETCH_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
        meta: { address },
      },
      {
        type: FETCH_CONTRACT_FAILURE,
        payload: handleError,
        meta: { address },
      },
    ],
  });

/**
 * Fetch contracts. Optionally specify a specific partner you want the contracts for.
 *
 * @param {number} start
 * @param {number} count
 * @param {string} [partnerAddress] - Partner address.
 * @param {string} filterStatus - Status applied in the filter.
 * @param {string} filterSilo
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchContracts = ({
  start = 0,
  count = CONTRACT_PAGE_SIZE,
  partnerAddress,
  filterStatus,
  filterSilo,
} = {}) =>
  createAction({
    endpoint: partnerAddress
      ? `${window.config.apiUrl}/partners/${partnerAddress}/contracts?${buildQueryString({
          start,
          count,
        })}`
      : `${window.config.apiUrl}/contracts?${buildQueryString({
          start,
          count,
          filterStatus,
          filterSilo,
        })}`,
    method: 'GET',
    types: [
      { type: FETCH_CONTRACTS_REQUEST },
      {
        type: start === 0 ? FETCH_CONTRACTS_SUCCESS : FETCH_CONTRACTS_UPDATE_SUCCESS,
        payload: (action, state, res) => handleResponse([contractSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      {
        type: FETCH_CONTRACTS_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Fetch contract products.
 *
 * @param {string} [contractAddress] - Contract address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchContractProducts = contractAddress =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/products`,
    method: 'GET',
    types: [
      { type: FETCH_CONTRACTPRODUCTS_REQUEST },
      {
        type: FETCH_CONTRACTPRODUCTS_SUCCESS,
      },
      {
        type: FETCH_CONTRACTPRODUCTS_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Fetch contract activity.
 *
 * @param {string} [contractAddress] - Contract address.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchContractActivity = contractAddress =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/activity`,
    method: 'GET',
    types: [
      { type: FETCH_CONTRACTACTIVITY_REQUEST },
      {
        type: FETCH_CONTRACTACTIVITY_SUCCESS,
        payload: (action, state, res) => handleResponse([activitySchema], action, state, res),
        meta: { contractAddress },
      },
      {
        type: FETCH_CONTRACTACTIVITY_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Create a new single contract related to a specific Partner
 *
 * @param {object} values - Object containing contract form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createContract = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_CONTRACT_REQUEST },
      {
        type: CREATE_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: CREATE_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Create a new single contract related to a specific Partner
 *
 * @param {string} contractAddress
 * @param {object} values - Object containing contract form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editContract = (contractAddress, values) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}`,
    method: 'PUT',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_CONTRACT_REQUEST },
      {
        type: EDIT_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: EDIT_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Submits a contract and all of the associated agreements for approval.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const submitForApproval = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/submit-for-approval`,
    method: 'PATCH',
    body: JSON.stringify(requestBody),
    types: [
      { type: SUBMIT_FOR_APPROVAL_REQUEST },
      {
        type: SUBMIT_FOR_APPROVAL_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: SUBMIT_FOR_APPROVAL_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Approve a new contract & agreements, or approve edits to an existing contract.
 *
 * @param {string} contractAddress - Eth address of Contract.
 * @param {boolean} [isEdit] - True if you're approving an edit, and not a newly onboarded contract.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const approve = (contractAddress, isEdit = false) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/approve`,
    method: 'PATCH',
    types: [
      { type: APPROVE_CONTRACT_REQUEST },
      {
        type: APPROVE_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
        meta: { isEdit },
      },
      {
        type: APPROVE_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Reject a new contract & agreements, or reject edits to an existing contract.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
// eslint-disable-next-line default-param-last
export const reject = (contractAddress, isEdit = false, rejectionBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/reject`,
    method: 'PATCH',
    body: JSON.stringify(rejectionBody),
    types: [
      { type: REJECT_CONTRACT_REQUEST },
      {
        type: REJECT_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
        meta: { isEdit, contractAddress },
      },
      {
        type: REJECT_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Pauses a contract, contract must currently be in Approved status
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const pause = contractAddress =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/pause`,
    method: 'PATCH',
    types: [
      { type: PAUSE_CONTRACT_REQUEST },
      {
        type: PAUSE_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: PAUSE_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

export const patchContractFilters = (contractAddress, scopeFilter) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/filters`,
    body: JSON.stringify({ scopeFilter }),
    method: 'PATCH',
    types: [
      { type: PATCH_CONTRACT_FILTERS_REQUEST },
      {
        type: PATCH_CONTRACT_FILTERS_SUCCESS,
        payload: (action, state, res) => res.json(),
        meta: { contractAddress },
      },
      { type: PATCH_CONTRACT_FILTERS_FAILURE, payload: handleError },
    ],
  });

/**
 * Unpauses a contract, contract must currently be in Paused status
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const unpause = contractAddress =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/unpause`,
    method: 'PATCH',
    types: [
      { type: UNPAUSE_CONTRACT_REQUEST },
      {
        type: UNPAUSE_CONTRACT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: UNPAUSE_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Discard the draft contract with the given address
 * @param {string} contractAddress - address of Contract.
 * @param {boolean} isEdit - True if it is a revised contract that has a previousState
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const discardContractDraft = (contractAddress, isEdit) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/discard-draft`,
    method: 'PATCH',
    types: [
      { type: DISCARD_CONTRACT_DRAFT_REQUEST },
      {
        type: DISCARD_CONTRACT_DRAFT_SUCCESS,
        payload: isEdit
          ? (action, state, res) => handleResponse(contractSchema, action, state, res)
          : undefined, // no payload for onboarded contract discard, since we delete the contract
        meta: !isEdit ? (action, state, res) => ({ resMessage: res.message }) : undefined,
      },
      {
        type: DISCARD_CONTRACT_DRAFT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * v2 contracts api. Re-orders agreements in the specified order and adds
 * a fallback agreement to the contract
 *
 * @param {string} contractAddress
 * @param {object} requestBody - the request body
 * @param {string[]} requestBody.agreementsOrder - the ids of the agreements ordered by user preference
 * @param {object} requestBody.fallbackAgreement - the fallback agreement object with calc method an params
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const reorderAgreements = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/agreements/re-order`,
    method: 'PATCH',
    body: JSON.stringify(requestBody),
    types: [
      { type: RE_ORDER_AGREEMENTS_CONTRACT_REQUEST },
      {
        type: RE_ORDER_AGREEMENTS_CONTRACT_SUCCESS,
        payload: (action, state, res) => res.text(),
      },
      {
        type: RE_ORDER_AGREEMENTS_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * v2 contracts api. Re-orders advance groups in the specified order and adds
 *
 * @param {string} contractAddress
 * @param {object} requestBody - the request body
 * @param {string[]} requestBody.advanceGroups - the ids of the advanceGroups ordered by user preference
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const reorderAdvanceGroups = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/advancegroups/re-order`,
    method: 'PATCH',
    body: JSON.stringify(requestBody),
    types: [
      { type: RE_ORDER_ADVANCEGROUPS_CONTRACT_FAILURE },
      {
        type: RE_ORDER_ADVANCEGROUPS_CONTRACT_FAILURE,
        payload: (action, state, res) => res.text(),
      },
      {
        type: RE_ORDER_ADVANCEGROUPS_CONTRACT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Create the fallback agreement to the contract
 *
 * @param {object} requestBody - the request body
 * @param {object} requestBody.fallbackAgreement - the fallback agreement object with calc method an params
 * @param {string} contractAddress - address of Contract.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createFallbackAgreement = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/fallbackAgreement`,
    method: 'POST',
    body: JSON.stringify(requestBody),
    types: [
      { type: SAVE_FALLBACK_AGREEMENT_REQUEST },
      {
        type: SAVE_FALLBACK_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: SAVE_FALLBACK_AGREEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Update the fallback agreement to the contract
 *
 * @param {object} requestBody - the request body
 * @param {object} requestBody.fallbackAgreement - the fallback agreement object with calc method an params
 * @param {string} contractAddress - address of Contract.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const updateFallbackAgreement = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/fallbackAgreement`,
    method: 'PUT',
    body: JSON.stringify(requestBody),
    types: [
      { type: UPDATE_FALLBACK_AGREEMENT_REQUEST },
      {
        type: UPDATE_FALLBACK_AGREEMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: UPDATE_FALLBACK_AGREEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Fetch associated products in a contract
 *
 * @param {string} address
 * @param {object} requestBody - the request body
 * @return {object} - Collection of products objects associated with a contract
 */
export const fetchAssociatedProducts = (address, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${address}/associatedProducts`,
    method: 'POST',
    body: JSON.stringify(requestBody),
    types: [
      { type: FETCH_ASSOCIATED_PRODUCTS_REQUEST },
      {
        type: FETCH_ASSOCIATED_PRODUCTS_SUCCESS,
        payload: (action, state, res) => handleResponse([productSchema], action, state, res),
      },
      {
        type: FETCH_ASSOCIATED_PRODUCTS_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Update details of an uploaded attachment to a contract .
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const uploadContractAttachment = (contractAddress, requestBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/contracts/${contractAddress}/associated-attachments`,
    method: 'PATCH',
    body: JSON.stringify(requestBody),
    types: [
      { type: UPLOAD_CONTRACT_ATTACHMENT_REQUEST },
      {
        type: UPLOAD_CONTRACT_ATTACHMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(contractSchema, action, state, res),
      },
      {
        type: UPLOAD_CONTRACT_ATTACHMENT_FAILURE,
        payload: handleError,
      },
    ],
  });
